import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import type { BusinessScope } from "../utils/businessResourceFreshness";

export type OnlineProductIdentity = { id: number; name: string };
export type OnlineStoreIdentity = { id: number; store_name: string; store_slug?: string };
export type OnlineSettingsTriggerProps = {
  onEditProduct?: (product: OnlineProductIdentity, store: OnlineStoreIdentity | null) => void;
  settingsRevision?: number;
};
export type StockMode = "standard" | "pre_order" | "unlimited";
export type OnlineProductValues = {
  online_visibility: boolean; online_featured: boolean; online_price: number | null;
  online_description: string; online_min_quantity: number | null; online_max_quantity: number | null;
  stock_management_mode: StockMode; online_seo_title: string; online_seo_description: string;
};
export type OnlineProduct = OnlineProductIdentity & OnlineProductValues;
export type OnlineProductResource = { product: OnlineProduct; featuredCount: number; featuredLimit: number; storeName: string };
const enabled = (value: unknown) => value === true || value === 1 || value === "1";
export const onlineValues = (row: any): OnlineProductValues => ({
  online_visibility: enabled(row.online_visibility), online_featured: enabled(row.online_featured),
  online_price: Number(row.online_price ?? row.price ?? 0),
  online_description: row.online_description ?? row.description ?? "",
  online_min_quantity: Number(row.online_min_quantity ?? 1),
  online_max_quantity: row.online_max_quantity === null || row.online_max_quantity === undefined || row.online_max_quantity === "" ? null : Number(row.online_max_quantity),
  stock_management_mode: row.stock_management_mode || "standard",
  online_seo_title: row.online_seo_title || row.name || "",
  online_seo_description: row.online_seo_description || row.description || "",
});
export const onlineFingerprint = (product: OnlineProduct) => JSON.stringify([product.id, product.name, onlineValues(product)]);
export function onlinePatch(values: OnlineProductValues, baseline: OnlineProduct, canEdit: boolean, canPublish: boolean) {
  const previous = onlineValues(baseline);
  const patch: Partial<OnlineProductValues> = {};
  (Object.keys(previous) as Array<keyof OnlineProductValues>).forEach((key) => {
    if ((key === "online_visibility" ? canPublish : canEdit) && values[key] !== previous[key]) {
      Object.assign(patch, { [key]: values[key] });
    }
  });
  return patch;
}
export async function readOnlineProduct(productId: number, storeId: number | null, scope: BusinessScope, signal: AbortSignal): Promise<OnlineProductResource> {
  const { data } = await apiClient.get(API_CONFIG.apiUrl("/produits/ecommerce"), { signal,
    headers: { "X-Company-Id": String(scope.companyId || "") },
    params: { product_id: productId, warehouse_id: scope.warehouseId, ...(storeId ? { store_id: storeId } : {}),
      page: 1, limit: 1, visibility: "all", featured: "all" } });
  if (!data.success || !Array.isArray(data.products) || data.products.length > 1 || data.featured_count == null ||
      !Number.isFinite(Number(data.featured_count)) || Number(data.featured_count) < 0 || !Number.isSafeInteger(Number(data.featured_limit ?? 10)) || Number(data.featured_limit ?? 10) <= 0) {
    throw new Error("Les paramètres de cette fiche n'ont pas pu être vérifiés.");
  }
  const context = data.featured_scope;
  if ((storeId && (context?.type !== "store" || Number(context.store_id) !== storeId)) ||
      (!storeId && (context?.type !== "warehouse" || Number(context.warehouse_id) !== Number(scope.warehouseId)))) {
    throw new Error("Le contexte de cette fiche a changé. Rouvrez-la depuis le catalogue.");
  }
  const product = data.products[0];
  if (!product || Number(product.id) !== productId || (product.company_id !== undefined && Number(product.company_id) !== Number(scope.companyId))) {
    throw new Error("Cette fiche n'est plus disponible dans l'entreprise et la boutique sélectionnées.");
  }
  return { product: { id: Number(product.id), name: product.name, ...onlineValues(product) },
    featuredCount: Number(data.featured_count), featuredLimit: Number(data.featured_limit ?? 10), storeName: context.store_name || "" };
}
export const onlineRequestError = (cause: any, fallback: string) => cause?.response?.data?.error || cause?.message || fallback;
